import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Flex } from '@radix-ui/themes'

import Vehicle from '../interfaces/Vehicle' // Adjust the import path as needed
import useFileUpload, { UserOrganization } from '../hooks/useFileUpload'
import { useAuth } from '../hooks/useAuth'
import { Transfer } from '../interfaces/Transfer'
import { TransferStatusBadge } from '../modules/transfers/components/TransferStatusBadge'
import { makeUrlTransfers } from '../hooks/connectionData'
import { customFetch } from '../utils/customFetch'
import { AppPath } from '../enums/AppPath'
import { ExpenseFormat, Expense } from '../interfaces/VehicleBase'
import { fechaIsoToYYYY_MM_DD, formatCurrency, formatDate, formatPeriod, formatWithThousandSep } from '../utils/utils'
import useVehiclePoster from '../hooks/useVehiclePoster'
import usePictureDelete from '../hooks/useImageDelete'
import useImageFetcher from '../hooks/useImageFetcher'
import useYapoPublisher from '../hooks/useYapoPublisher'
import useChaPublisher from '../hooks/useChaPublisher'
import { PageState } from '../interfaces/PageState'
import useMeliPublisher from '../hooks/useMeliPublisher'
import useLinzePublisher from '../hooks/useLinzePublisher'
import { UserProfileRole } from '../modules/users/types/UserProfile'
import { chaConsumablesWarning } from '../utils/chaProducts'

import { ButtonAction, ButtonCheckConfirma, ButtonDelete, ButtonMenuSet } from './Buttons'
import SettlementCost from './SettlementCost'
import Yapo from './Yapo'
import { DocStatusHeaderEx } from './DocStatusHeader'
import ConfirmationModal from './ConfirmationModal'
import InputField from './InputField'
import { Info } from './MessageInfo'
import InputTextArea from './InputTextArea'
import DisplayError from './DisplayError'
import Cha from './Cha'
import VehicleImages from './VehicleImages'
import { DisplayValueEx } from './DisplayValue'
import Meli from './Meli'
import LinzeForm from './LinzeForm'
import ChileAutosProducts from './ChileAutosProducts'
import VehicleVideoEnhanced from './VehicleVideoEnhanced'
import VehicleAuditHistory from './VehicleAuditHistory'

interface VehicleDetailProps {
  vehicle: Vehicle
  onRefresh: () => void
  clickedSubMenuIndex: number
  setClickedSubMenuIndex: React.Dispatch<React.SetStateAction<number>>
}

const VehicleDetail: React.FC<VehicleDetailProps> = ({
  vehicle,
  onRefresh,
  clickedSubMenuIndex,
  setClickedSubMenuIndex,
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [confirmationModalOpenCha, setConfirmationModalOpenCha] = useState<boolean>(false)
  const [confirmationModalOpenMeli, setConfirmationModalOpenMeli] = useState<boolean>(false)
  const [confirmationModalOpenLinze, setConfirmationModalOpenLinze] = useState<boolean>(false)
  const { yapoDelete, errorYapoDelete } = useYapoPublisher()
  const { chaDelete, errorChaDelete } = useChaPublisher()
  const { meliDelete, errorMeliDelete } = useMeliPublisher()
  const { linzeDelete, errorLinzeDelete } = useLinzePublisher()
  const { userData, hasRole } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Por si viene un estado
  const pageState: PageState = location.state

  // Get current user job and organization
  const userJob = userData?.profile?.job || ''
  const userOrgId = userData?.organization?._id || ''

  // Check if user is an owner of the organization to which the vehicle belongs
  const isOrgOwner = userJob === 'JOB.OWNER' && userOrgId === vehicle.organization

  // Check if current user's organization can access Linze
  const LINZE_ENABLED_ORGANIZATIONS = [
    '66f45db62b168a0ea2cbf9a5', // Authorized organization 1
    '5a628323cdb9dd31b15b85a8', // Authorized organization 2
  ]
  const canAccessLinze = LINZE_ENABLED_ORGANIZATIONS.includes(userOrgId)

  //Aca seteamos el estado mode : string
  const handleEditar = () => {
    const mode = true
    navigate(`/${AppPath.Vehicles}/edit/${vehicle._id}`, { state: { mode, pageState } })
  }

  // Devolvemos el estado
  const handleGoBack = () => {
    if (pageState) {
      const stateString = JSON.stringify(pageState)
      const encodedState = encodeURIComponent(stateString)
      navigate(`${pageState.url}?pageState=${encodedState}`)
    }
  }
  const costoTotal = (vehicle.expenses ?? []).reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)
  const costoTotal_format = formatCurrency(costoTotal)

  // Allow view access if user has role permission OR is an organization owner
  const canViewPurchasePrice = hasRole(UserProfileRole.ViewPurchasesPrice) || isOrgOwner
  const canModify = hasRole(UserProfileRole.EditVehicles)
  /*
  Archivar y restaurar van juntos a propósito: si el permiso cubriera sólo archivar,
  cualquiera con "Gestionar vehículos" podría restaurar lo que otro archivó y la
  restricción no serviría de nada. Lo que de verdad lo impide está en la API
  (vehicleArchiveRoute / vehicleRestoreRoute); esto sólo esconde el botón.
  */
  const canArchive = canModify && hasRole(UserProfileRole.ArchiveVehicles)

  const archived = !!vehicle.archivedAt
  const sold = !!vehicle.soldAt
  let title = ''
  let date = ''
  let by = ''
  let cause = ''

  if (archived) {
    title = 'Este vehículo fue archivado'
    date = vehicle.archivedAt ?? ''
    cause = vehicle.archivedCause ?? ''
    by = vehicle.archivedBy_user ?? ''
  }

  const today = new Date()
  const startDate = new Date(formatDate(vehicle?.documents.checkup))
  const meses = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth())
  const esCompra = vehicle.state === 'STATE.VEHICLE.PURCHASED'
  const mesesPalabra = meses === 1 ? 'mes' : 'meses'
  const atrasado = meses > 0 ? ` (hace ${meses} ${mesesPalabra})` : ''
  const vencimientoRevision = `${formatPeriod(formatDate(vehicle?.documents.checkup))}${atrasado}`
  const published_local = vehicle.publishedAt ? 'Si' : 'No'
  /*
  Una baja de Yapo no es instantánea EN NUESTROS DATOS. Medido el 2026-08-25
  sobre un aviso real: Yapo lo baja en menos de 30 segundos, pero recién damos
  el vehículo por no publicado cuando la página pública del aviso lo confirma,
  y eso llega a los ~45s-1min. `handleYapoDeleteConfirm` refresca la ficha en
  t=0, justo cuando la respuesta todavía es "publicado".

  Sin distinguir ese estado, el usuario apretaba Eliminar, la página se
  refrescaba y volvía idéntica —con el link y el botón Eliminar en su lugar—, así
  que la conclusión razonable era "no funciona". De ahí las bajas repetidas y las
  republicaciones para "resetear" el aviso. El aviso ya estaba retirado.
  */
  const isRetiring_yapo = vehicle.unpublishStatus?.yapo?.state === 'pending'
  const published_yapo = isRetiring_yapo ? 'Retirando…' : vehicle.external_yapo?.publishedAt ? 'Si' : 'No'
  const isPublished_yapo = !isRetiring_yapo && !!vehicle.external_yapo?.publishedAt
  const hasYapoLink = !!vehicle.external_yapo?.adLink?.link

  const published_cha = vehicle.external_chileautos?.publishedAt ? 'Si' : 'No'
  const isPublished_cha = published_cha === 'Si'
  const hasChaIdentifier = !!vehicle.external_chileautos?.identifier
  const published_meli = vehicle.external_meli?.publishedAt ? 'Si' : 'No'
  const isPublished_meli = published_meli === 'Si'
  const hasMeliPermalink = !!vehicle.external_meli?.permalink

  const published_linze = vehicle.external_linze?.publishedAt ? 'Si' : 'No'
  const isPublished_linze = published_linze === 'Si'
  const hasLinzeLink = !!vehicle.external_linze?.trackingLink

  // ChileAutos permalink construction
  const makeChaUrl = (identifier: string) => {
    return `https://www.chileautos.cl/details/${identifier}`
  }

  /*
   const make_adLink_cha = (ident:string) => {
  
      const validateUrl:string = import.meta.env.VITE_CHA_PUBLICATION_VALIDATE_URL;
      const validUrl = validateUrl.replace("{ident}", ident);
      return validUrl;
    }
    const adLink_cha = make_adLink_cha(vehicle.external_chileautos?.identifier?? "");
    const adLink_yapo = vehicle.external_yapo?.adLink;
    const AdShow = (url:string) => {
  
      window.open(url, '_blank', 'noopener,noreferrer');
  
    }
      */
  const yapoDeleteClick = () => {
    setConfirmationModalOpen(true)
  }
  const chaDeleteClick = () => {
    setConfirmationModalOpenCha(true)
  }
  const meliDeleteClick = () => {
    setConfirmationModalOpenMeli(true)
  }
  const linzeDeleteClick = () => {
    setConfirmationModalOpenLinze(true)
  }

  // Vamos a yapo a borrar este aviso
  const handleYapoDeleteConfirm = async () => {
    setConfirmationModalOpen(false)
    const resp = await yapoDelete(vehicle._id)

    if (resp) {
      onRefresh()
    }
  }
  const handleYapoDeleteCancel = async () => {
    setConfirmationModalOpen(false)
  }
  const handleChaDeleteConfirm = async () => {
    setConfirmationModalOpenCha(false)
    const resp = await chaDelete(vehicle._id)

    if (resp) {
      onRefresh()
    }
  }
  const handleChaDeleteCancel = async () => {
    setConfirmationModalOpenCha(false)
  }

  const handleMeliDeleteConfirm = async () => {
    setConfirmationModalOpenMeli(false)
    const resp = await meliDelete(vehicle._id, userData?.profile?._id ?? '')

    if (resp) {
      onRefresh()
    }
  }
  const handleMeliDeleteCancel = async () => {
    setConfirmationModalOpenMeli(false)
  }

  const handleLinzeDeleteConfirm = async () => {
    setConfirmationModalOpenLinze(false)
    const resp = await linzeDelete(vehicle._id)

    if (resp) {
      onRefresh()
    }
  }
  const handleLinzeDeleteCancel = async () => {
    setConfirmationModalOpenLinze(false)
  }

  return (
    <Flex direction="column">
      <DocStatusHeaderEx title={title} date={date} cause={cause} by={by} />
      <div className="text-blue-500 font-bold text-xl mb-2">
        {vehicle?.brand} {vehicle?.model} {vehicle?.version} ({vehicle?.year})
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {/* First Column */}
        <div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Tipo:</span>
            <p>{vehicle?.type_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Carrocería:</span>
            <p>{vehicle?.body_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Color:</span>
            <p>{vehicle?.color_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Kilometraje:</span>
            <p>{vehicle?.odometer_format} km</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Transmisión:</span>
            <p>{vehicle?.gearbox_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Combustible:</span>
            <p>{vehicle?.fuel_format}</p>
          </div>

          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Patente:</span>
            <p>{vehicle?.plate}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Chasis:</span>
            <p>{vehicle?.chassisId}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Motor:</span>
            <p>{vehicle?.engineId}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Propietarios:</span>
            <p>{vehicle?.owners}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Llaves:</span>
            <p>{vehicle?.keys}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Comentarios:</span>
            <p>{vehicle?.comments}</p>
          </div>
        </div>

        {/* Second Column */}
        <div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Sucursal:</span>
            <p>{vehicle?.branch_name}</p>
          </div>

          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Estado de propiedad:</span>
            <p>{vehicle?.state_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Avalúo:</span>
            <p>{vehicle?.appraisal_format}</p>
          </div>
          {esCompra ? (
            canViewPurchasePrice ? (
              <div className="mb-2 flex items-center">
                <span className="font-bold mr-2">Precio compra:</span>
                <p>{vehicle?.pricePurchased_format}</p>
              </div>
            ) : null
          ) : (
            <div className="mb-2 flex items-center">
              <span className="font-bold mr-2">Precio cliente</span>
              <p>{vehicle?.priceConsignment_format}</p>
              <span className="ml-1"> (última consignación)</span>
            </div>
          )}

          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Precio publicación:</span>
            <p>{vehicle?.price_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Precio sugerido:</span>
            <p>{vehicle?.suggestedPrice_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Gasto total:</span>
            <p>{costoTotal_format}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Estado del seguro:</span>
            <p>{vehicle?.documents.insurance}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Estado del permiso:</span>
            <p>{vehicle?.documents.permit}</p>
          </div>

          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Vencimiento revisión:</span>
            <p>{vencimientoRevision}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Estado de mantenciones:</span>
            <p>{vehicle?.maintenance.status}</p>
          </div>
          <div className="mb-2 flex items-center">
            <span className="font-bold mr-2">Detalle de mantenciones:</span>
            <p>{vehicle?.maintenance.comments}</p>
          </div>
        </div>

        {/* Refactored Third Column */}
        <div>
          <div className="mb-4">
            <span className="font-bold">Características:</span>
            <ul className="list-disc list-inside">
              {vehicle?.characteristics?.map((chars: string, index: number) => (
                <li key={`${index}-${chars}`}>{chars}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {!esCompra && (
        <div>
          <div className="text-xl font-bold">Datos del cliente</div>
          <div className="grid grid-cols-[auto,1fr] gap-x-4">
            <DisplayValueEx label="Nombre" value={`${vehicle.client?.firstname} ${vehicle.client?.lastname}`} />
            <DisplayValueEx label="Teléfono" value={vehicle.client?.phone} />
            <DisplayValueEx label="Email" value={vehicle.client?.email} />
          </div>
        </div>
      )}
      <div>
        <div className="text-xl font-bold">Datos de publicación</div>
        <div className="grid grid-cols-[auto__auto_1fr] gap-x-4">
          {/** Eliminamos la columna "ver", porque funciona solo para Yapo.
          <div>Sitio Web</div> <div>{published_local}</div><div></div><div></div>
          <DisplayValueEx label="Yapo" value={published_yapo} /> <div>{isPublished_yapo && (
            <button onClick={() => AdShow(adLink_yapo?.link??"")} className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200">Ver</button>
          )}</div>
          <div>{isPublished_yapo && (<button onClick={yapoDeleteClick} className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200">Eliminar</button>
          )}</div>
          <DisplayValueEx label="Chileautos" value={published_cha} /> <div>{isPublished_cha && (
            <button onClick={() => AdShow(adLink_cha ??"")} className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200">Ver</button>
          )}</div>
          <div>{isPublished_cha && (<button onClick={chaDeleteClick} className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200">Eliminar</button>
          )}</div>
          */}
          <div>Sitio Web</div> <div>{published_local}</div>
          <div></div>
          <DisplayValueEx label="Yapo" value={published_yapo} />
          <div>
            {isPublished_yapo && hasYapoLink && (
              <button
                onClick={() => window.open(vehicle.external_yapo?.adLink?.link, '_blank', 'noopener,noreferrer')}
                className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200 mr-4"
              >
                Visitar
              </button>
            )}
            {isPublished_yapo && (
              <button
                onClick={yapoDeleteClick}
                className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200"
              >
                Eliminar
              </button>
            )}
            {isRetiring_yapo && (
              <span className="text-gray-500 italic" title="El aviso ya se dio de baja en Yapo. Estamos confirmando contra la página pública antes de marcarlo como no publicado.">
                Retirando de Yapo… se confirma en menos de un minuto
              </span>
            )}
          </div>
          <DisplayValueEx label="Chileautos" value={published_cha} />
          <div>
            {isPublished_cha && hasChaIdentifier && (
              <button
                onClick={() =>
                  window.open(
                    makeChaUrl(vehicle.external_chileautos?.identifier ?? ''),
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200 mr-4"
              >
                Visitar
              </button>
            )}
            {isPublished_cha && (
              <button
                onClick={chaDeleteClick}
                className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200"
              >
                Eliminar
              </button>
            )}
          </div>
          <DisplayValueEx label="MercadoLibre " value={published_meli} />
          <div>
            {isPublished_meli && hasMeliPermalink && (
              <button
                onClick={() => window.open(vehicle.external_meli?.permalink, '_blank', 'noopener,noreferrer')}
                className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200 mr-4"
              >
                Visitar
              </button>
            )}
            {isPublished_meli && (
              <button
                onClick={meliDeleteClick}
                className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200"
              >
                Eliminar
              </button>
            )}
          </div>
          {canAccessLinze && (
            <>
              <DisplayValueEx label="Linze" value={published_linze} />
              <div>
                {isPublished_linze && hasLinzeLink && (
                  <button
                    onClick={() => window.open(vehicle.external_linze?.trackingLink, '_blank', 'noopener,noreferrer')}
                    className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200 mr-4"
                  >
                    Visitar
                  </button>
                )}
                {isPublished_linze && (
                  <button
                    onClick={linzeDeleteClick}
                    className="text-blue-500 underline hover:text-blue-700 transition-colors duration-200"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </>
          )}
          {/* 
             <div>Sitio Web</div> <div>{published_local}</div>
          <DisplayValueEx label="Yapo" value={published_yapo} /> 
          <DisplayValueEx label="Chileautos" value={published_cha} /> 
      */}
        </div>
      </div>
      <div className="flex justify-center gap-4">
        {canModify && !archived && !sold && <ButtonAction label={'Modificar..'} handleButtonClick={handleEditar} />}
        {pageState && <ButtonAction label="Volver" handleButtonClick={handleGoBack} />}
      </div>
      {confirmationModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar el aviso en Yapo?"
          onConfirm={handleYapoDeleteConfirm}
          onCancel={handleYapoDeleteCancel}
        />
      )}
      {errorYapoDelete && <DisplayError msg={errorYapoDelete?.message ?? ''} />}
      {errorChaDelete && <DisplayError msg={errorChaDelete?.message ?? ''} />}
      {errorMeliDelete && <DisplayError msg={errorMeliDelete?.message ?? ''} />}
      {canAccessLinze && errorLinzeDelete && <DisplayError msg={errorLinzeDelete?.message ?? ''} />}

      {confirmationModalOpenCha && (
        <ConfirmationModal
          message={[chaConsumablesWarning(vehicle), '¿Estás seguro de eliminar el aviso en Chileautos?']
            .filter(Boolean)
            .join(' ')}
          onConfirm={handleChaDeleteConfirm}
          onCancel={handleChaDeleteCancel}
        />
      )}

      {confirmationModalOpenMeli && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar el aviso en MercadoLibre?"
          onConfirm={handleMeliDeleteConfirm}
          onCancel={handleMeliDeleteCancel}
        />
      )}

      {canAccessLinze && confirmationModalOpenLinze && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar el aviso en Linze?"
          onConfirm={handleLinzeDeleteConfirm}
          onCancel={handleLinzeDeleteCancel}
        />
      )}

      <SubMenu
        vehicle={vehicle}
        canModify={canModify}
        canArchive={canArchive}
        onRefresh={onRefresh}
        clickedSubMenuIndex={clickedSubMenuIndex}
        setClickedSubMenuIndex={setClickedSubMenuIndex}
        canAccessLinze={canAccessLinze}
      />
    </Flex>
  )
}

/*
Muestra y maneja el submenú del vehiculo en modo de Modificación
*/
const SubMenu: React.FC<{
  vehicle: Vehicle
  canModify: boolean
  canArchive: boolean
  onRefresh: () => void
  clickedSubMenuIndex: number
  setClickedSubMenuIndex: React.Dispatch<React.SetStateAction<number>>
  canAccessLinze: boolean
}> = ({ vehicle, canModify, canArchive, onRefresh, clickedSubMenuIndex, setClickedSubMenuIndex, canAccessLinze }) => {
  const { userData } = useAuth()
  const transfersEnabled = ['5a628323cdb9dd31b15b85a8', '66b51a545e7ba00ea85cc5b6'].includes(
    userData?.organization?._id || ''
  )

  const buttonCaptions = [
    'Imágenes Públicas',
    'Imágenes de Registro',
    'Video',
    'Documentos Adjuntos',
    'Ingresos',
    'Gastos',
    'Publicar', // ⬅️ Moved from position 8 to position 7
    'Externos', // ⬅️ Moved from position 7 to position 8
    'Historial',
  ]

  let transfersIndex = -1

  if (transfersEnabled) {
    buttonCaptions.push('Transferencias')
    transfersIndex = buttonCaptions.length - 1
  }

  const isArchived = !!vehicle.archivedAt
  let archiveIndex = -1

  if (canArchive) {
    buttonCaptions.push(isArchived ? 'Restaurar' : 'Archivar')
    archiveIndex = buttonCaptions.length - 1
  }
  const [motivo, setMotivo] = useState<string>('')
  const { vehicleArchive, vehicleArchiveError, vehicleRestore, vehicleRestoreError, loadingPost } =
    useVehiclePoster()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'motivo') setMotivo(value)
  }

  // Click en botón Archivar
  const handleArchivarClick = async (motivo: string) => {
    await vehicleArchive(vehicle._id, motivo)

    if (!vehicleArchiveError) {
      onRefresh()
    }
  }

  // Click en botón Archivar
  const handleRestaurarClick = async () => {
    await vehicleRestore(vehicle._id)

    if (!vehicleRestoreError) {
      onRefresh()
    }
  }

  /*
  `canArchive` va explícito y no alcanza con comparar índices: sin el permiso archiveIndex
  queda en -1, que es justo el valor inicial de clickedSubMenuIndex ("ninguna pestaña"),
  así que el formulario de archivar aparecía apenas se abría el vehículo.
  */
  const showArchivar = canArchive && clickedSubMenuIndex === archiveIndex && !isArchived
  const showRestaurar = canArchive && clickedSubMenuIndex === archiveIndex && isArchived
  const esVendido = !!vehicle.soldAt

  return (
    <div className="mt-2 p-2 mx-auto shadow-md border">
      <ButtonMenuSet
        captions={buttonCaptions}
        clickedIndex={clickedSubMenuIndex}
        handleButtonClick={(n) => setClickedSubMenuIndex(n)}
        short={true}
      />

      <div className="mt-4">
        {clickedSubMenuIndex === 0 && (
          <VehicleImages vehicle={vehicle} canModify={canModify} registry={false} onRefresh={onRefresh} />
        )}
        {clickedSubMenuIndex === 1 && (
          <VehicleImages vehicle={vehicle} canModify={canModify} registry={true} onRefresh={onRefresh} />
        )}
        {clickedSubMenuIndex === 2 && (
          <VehicleVideoEnhanced vehicle={vehicle} canModify={canModify} onRefresh={onRefresh} />
        )}
        {clickedSubMenuIndex === 3 && (
          <DocumentosAdjuntos vehicle={vehicle} canModify={canModify} onRefresh={onRefresh} />
        )}
        {clickedSubMenuIndex === 4 && (
          <Gastos vehicle={vehicle} isIncome={true} canModify={canModify} onRefresh={onRefresh} />
        )}
        {clickedSubMenuIndex === 5 && (
          <Gastos vehicle={vehicle} isIncome={false} canModify={canModify} onRefresh={onRefresh} />
        )}
        {clickedSubMenuIndex === 6 && <Publicar vehicle={vehicle} />} {/* ⬅️ Changed from index 7 to 6 */}
        {clickedSubMenuIndex === 7 && (
          <Externos vehicle={vehicle} onRefresh={onRefresh} canAccessLinze={canAccessLinze} />
        )}{' '}
        {/* ⬅️ Changed from index 6 to 7 */}
        {clickedSubMenuIndex === 8 && <VehicleAuditHistory vehicleId={vehicle._id} />}
        {transfersEnabled && clickedSubMenuIndex === transfersIndex && (
          <VehicleTransferHistory vehicleId={vehicle._id} />
        )}
        {showArchivar && (
          <>
            {!esVendido && (
              <ButtonCheckConfirma
                title={'Archivar vehículo'}
                subTitle={[
                  'Al archivar el vehículo será des-publicado, y no podrá ser vendido.',
                  chaConsumablesWarning(vehicle),
                ]
                  .filter(Boolean)
                  .join(' ')}
                checkLabel={`Estoy seguro de archivar este vehículo.`}
                buttonLabel={'Archivar'}
                handleCrearClick={() => handleArchivarClick(motivo)}
                handleTextAreaChange={handleInputChange}
                textAreaLabel={`Motivo`}
                textAreaName="motivo"
                textAreaValue={motivo}
                disabled={loadingPost}
              />
            )}
            {esVendido && <div className="text-2xl m-10">El vehículo fue vendido, no se puede archivar. </div>}
          </>
        )}
        {showRestaurar && (
          <ButtonCheckConfirma
            title={'Restaurar vehículo'}
            subTitle={'Al restaurar el vehículo será visible públicamente si está publicado, y podrá ser vendido.'}
            checkLabel={`Estoy seguro de restaurar este vehículo.`}
            buttonLabel={'Restaurar'}
            handleCrearClick={() => handleRestaurarClick()}
            disabled={loadingPost}
          />
        )}
      </div>
      <div>{vehicleArchiveError?.message}</div>
      <div>{vehicleRestoreError?.message}</div>
    </div>
  )
}

// Gastos o ingresos del vehiculo. La suma va tambian arriba en "Costo total"  cuando es "Costo"
const Gastos: React.FC<{
  vehicle: Vehicle
  isIncome: boolean
  canModify: boolean
  onRefresh: () => void
}> = ({ vehicle, isIncome, canModify, onRefresh }) => {
  const { vehicleExpensesPostError, vehicleExpensesPost } = useVehiclePoster()
  const [ready, setReady] = useState<boolean>(false)

  const transformToExpenseFormat = (expenses: Expense[]): ExpenseFormat[] => {
    return expenses.map((expense) => ({
      ...expense, // Spread the existing Expense properties
      cost_format: `${formatWithThousandSep(expense.cost)}`,
      date: fechaIsoToYYYY_MM_DD(expense.date),
    }))
  }
  const calcTotal = (array: ExpenseFormat[]): number => {
    return array.reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)
  }
  const expenseFormats: ExpenseFormat[] = transformToExpenseFormat(
    isIncome ? (vehicle.incomes ?? []) : (vehicle.expenses ?? [])
  )
  const totalIni = calcTotal(expenseFormats)
  const [total, setTotal] = useState<number>(totalIni)
  const [costs, setCosts] = useState<ExpenseFormat[]>(expenseFormats)

  /*
  llega cuando hubo un cambio en los Costos
  */
  const handleCostsChange = async (updatedCosts: typeof costs) => {
    if (!ready) return
    setCosts(updatedCosts)
    const total = calcTotal(updatedCosts)
    setTotal(total)

    await vehicleExpensesPost(vehicle._id, updatedCosts, isIncome)
    onRefresh()
  }

  useEffect(() => {
    setReady(true)
  }, [])

  const title = isIncome ? 'Ingresos' : 'Gastos'

  return (
    <>
      <SettlementCost title={title} array={costs} onCostsChange={handleCostsChange} canModify={canModify} />
      <div className="grid grid-cols-6 border-t font-bold">
        <div className="pr-4 col-span-4 ">Total</div>
        <div className="pr-4 text-right">{formatCurrency(total)}</div>
      </div>
      {vehicleExpensesPostError?.message}
    </>
  )
}

/*
Publicaciones a sitios propio
*/
const Publicar: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => {
  const [promoMsg, setPromoMsg] = useState<string>(vehicle.promo?.message ?? '')
  const [disabled, setDisabled] = useState<boolean>(true)
  const { vehiclePublishLocal, vehiclePublishLocalError } = useVehiclePoster()
  const [infoMessage, setInfoMessage] = useState<string>('')

  // Cuando se escribe algo en el "Precio Venta" o la fecha
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { value } = e.target

    setPromoMsg(value)
    setDisabled(false)
  }
  // Publicamos en el sitio Web de la automotora
  const onPublishLocal = async () => {
    const resp = await vehiclePublishLocal(vehicle._id, promoMsg)

    if (resp) {
      setInfoMessage("Vehículo marcado como 'publicado'.")
      setDisabled(true)
    }
  }

  return (
    <div>
      <InputTextArea
        label={'Ingresa un mensaje promocional para los potenciales compradores de este vehículo.'}
        name={'promoMsg'}
        value={promoMsg}
        handleInputChange={handleInputChange}
      />
      <div>{`No ingreses datos de contacto ni enlaces.`}</div>
      <div className="flex justify-center mt-4">
        <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
      </div>
      <DisplayError msg={vehiclePublishLocalError?.message} />
      <ButtonAction label={'Publicar'} handleButtonClick={onPublishLocal} disabled={disabled} />
    </div>
  )
}
/*
Publicaciones a sitios externos, Yapo, Chileautos. etc
*/
const Externos: React.FC<{ vehicle: Vehicle; onRefresh: () => void; canAccessLinze: boolean }> = ({
  vehicle,
  onRefresh,
  canAccessLinze,
}) => {
  const [clickedIndex, setClickedIndex] = useState<number>(-1)

  // Build menu options based on permissions
  const menuOptions = canAccessLinze
    ? ['Chileautos', 'Yapo', 'MercadoLibre', 'Linze']
    : ['Chileautos', 'Yapo', 'MercadoLibre']

  return (
    <>
      <ButtonMenuSet
        captions={menuOptions}
        clickedIndex={clickedIndex}
        handleButtonClick={(n) => setClickedIndex(n)}
        short={true}
      />
      <div className="mt-4">
        {clickedIndex === 0 && (
          <div>
            <Cha vehicle={vehicle} onRefresh={onRefresh} />
            {/* Mismos destacados que en la pantalla de publicaciones: acá es donde se busca por un vehículo. */}
            <div className="mt-6 border-t border-gray-200 pt-4">
              <ChileAutosProducts vehicle={vehicle} onChange={onRefresh} />
            </div>
          </div>
        )}

        {clickedIndex === 1 && (
          <div>
            <Yapo vehicle={vehicle} onRefresh={onRefresh} />
          </div>
        )}
        {clickedIndex === 2 && (
          <div>
            <Meli vehicle={vehicle} onRefresh={onRefresh} />
          </div>
        )}
        {canAccessLinze && clickedIndex === 3 && (
          <div>
            <LinzeForm vehicle={vehicle} branch={null} onPublishSuccess={onRefresh} />
          </div>
        )}
      </div>
    </>
  )
}

// Doumentos adjuntos (attachments)
const DocumentosAdjuntos: React.FC<{
  vehicle: Vehicle
  canModify: boolean
  onRefresh: () => void
}> = ({ vehicle, canModify, onRefresh }) => {
  const { userData } = useAuth()
  const [description, setDescription] = useState<string>('')
  const { pictureDelete, errorPicture } = usePictureDelete()
  const userOrganization: UserOrganization = {
    organization_id: vehicle.organization,
    user_id: userData?.user?._id ?? '',
    vehicle_id: vehicle._id,
    registry: '0',
    attachment: '1',
  }
  const { fetchAttachment, errorImage } = useImageFetcher()
  const { isUploading, error, handleFileChangeEx } = useFileUpload(
    userOrganization,

    () => {
      // Callback to update image list after successful upload
      onRefresh()
    }
  )
  const onDeleteClick = async (pictureId: string, attachmentId: string): Promise<void> => {
    await pictureDelete(pictureId, vehicle._id, false, attachmentId)

    // lo sacamos de la lista
    if (!errorPicture) {
      onRefresh()
    }
  }
  const openDocInNewTab = async (id: string) => {
    await fetchAttachment(id)
  }

  // Para el delete
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [selectedFileId, setSelectedFileId] = useState<string>('')
  const errors: { [key: string]: string } = {}
  const deleteClick = async (pictureId: string, attachmentId: string) => {
    setSelectedFileId(attachmentId)
    setSelectedItemId(pictureId) // Store the item ID in the state
    setIsModalOpen(true)
  }
  const handleConfirm = async () => {
    setIsModalOpen(false)
    // Perform delete action here
    onDeleteClick(selectedItemId, selectedFileId)

    setSelectedItemId('')
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedItemId('')
  }
  // Despues de que el user selecciona un archivo
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await handleFileChangeEx(event, description)
  }

  return (
    <div>
      <div className="grid grid-cols-[auto,1fr] ml-20 mb-4 gap-4">
        {vehicle.attachments?.map((doc) => (
          <React.Fragment key={doc._id}>
            <div>
              <button onClick={() => openDocInNewTab(doc.file)}> {doc.name}</button>
            </div>

            <div>
              {canModify && <ButtonDelete key={doc.file} handleButtonClick={() => deleteClick(doc.file, doc._id)} />}
            </div>
          </React.Fragment>
        ))}
      </div>

      {canModify && (
        <div className="space-y-4">
          <InputField
            label="Descripción"
            name="description"
            type="string"
            value={description}
            handleInputChange={(e) => setDescription(e.target.value)}
            errors={errors}
          />
          {/* Custom styled button to replace input[type=file] */}
          <label
            htmlFor="file-upload"
            className={`block w-max px-4 py-2 cursor-pointer bg-green-500 text-white rounded hover:bg-green-600 ${
              (isUploading || !description) && 'opacity-50 cursor-not-allowed'
            }`}
          >
            {isUploading ? 'Cargando...' : 'Agregar documento...'}
          </label>

          <input
            id="file-upload"
            type="file"
            accept="*/*"
            onChange={handleFileChange}
            className="hidden" // Hide the default file input
            disabled={!description}
          />
          <div>Tamaño máximo 20 MB por archivo</div>
          {error && <p className="text-red-500">{error}</p>}
          {errorPicture && <p className="text-red-500">{errorPicture.message}</p>}
          {errorImage && <p className="text-red-500">{errorImage.message}</p>}
        </div>
      )}
      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar esta imagen?"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  )
}
/**
 * Shows transfer history for a vehicle.
 */
const VehicleTransferHistory: React.FC<{ vehicleId: string }> = ({ vehicleId }) => {
  const { userData } = useAuth()
  const navigate = useNavigate()
  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchTransfers = async () => {
      if (!userData?.organization?._id) return
      setLoading(true)
      try {
        const url = makeUrlTransfers(userData.organization._id)
        const data = await customFetch<{ transfers: Transfer[]; total: number }>(url)
        // Filter by this vehicle
        const vehicleTransfers = data.transfers.filter((t) => t.vehicle === vehicleId)
        setTransfers(vehicleTransfers)
      } catch {
        // silently fail
      } finally {
        setLoading(false)
      }
    }
    fetchTransfers()
  }, [vehicleId, userData?.organization?._id])

  if (loading) {
    return <div className="p-4 text-gray-500">Cargando transferencias...</div>
  }

  if (transfers.length === 0) {
    return <div className="p-4 text-gray-500">No hay transferencias para este vehículo.</div>
  }

  return (
    <div className="space-y-3">
      {transfers.map((transfer) => (
        <div
          key={transfer._id}
          className="border rounded-md p-3 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => navigate(`/${AppPath.Transfers}/view/${transfer._id}`)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">#{transfer.number}</span>
              <TransferStatusBadge status={transfer.status} />
            </div>
            <span className="text-sm text-gray-500">{new Date(transfer.createdAt).toLocaleDateString('es-CL')}</span>
          </div>
          <div className="text-sm text-gray-600 mt-1">
            Comprador: {transfer.buyer.name} | Total: ${transfer.totalCost.toLocaleString('es-CL')}
          </div>
          {transfer.transfieroId && (
            <div className="flex flex-col gap-1 mt-1">
              <div className="text-xs text-gray-400 flex items-center gap-1">
                <span>Transfiero ID: {transfer.transfieroId}</span>
              </div>

              {/* Buyer Signature Details */}
              {(transfer.buyerFirmaCode || transfer.buyerFirmaLink) && (
                <div
                  className="mt-1 flex items-center justify-between p-2 rounded bg-blue-50 border border-blue-100 transition-colors hover:bg-blue-100 hover:border-blue-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-blue-700">Comprador:</span>
                    {transfer.buyerFirmaCode && (
                      <span className="font-mono text-xs text-blue-900 bg-white px-1.5 py-0.5 rounded border border-blue-100">
                        {transfer.buyerFirmaCode}
                      </span>
                    )}
                  </div>
                  {transfer.buyerFirmaLink && (
                    <a
                      href={transfer.buyerFirmaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded transition-colors"
                    >
                      Ir a firmar
                    </a>
                  )}
                </div>
              )}

              {/* Seller Signature Details */}
              {(transfer.sellerFirmaCode || transfer.sellerFirmaLink) && (
                <div
                  className="mt-1 flex items-center justify-between p-2 rounded bg-emerald-50 border border-emerald-100 transition-colors hover:bg-emerald-100 hover:border-emerald-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-emerald-700">Vendedor:</span>
                    {transfer.sellerFirmaCode && (
                      <span className="font-mono text-xs text-emerald-900 bg-white px-1.5 py-0.5 rounded border border-emerald-100">
                        {transfer.sellerFirmaCode}
                      </span>
                    )}
                  </div>
                  {transfer.sellerFirmaLink && (
                    <a
                      href={transfer.sellerFirmaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded transition-colors"
                    >
                      Ir a firmar
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default VehicleDetail
